import "./styles.css";
import { EggArenaApp } from "./app";
import { analytics } from "./browser/analytics";
import { spriteMarkup } from "./browser/assets";
import { mountRunSetup } from "./browser/run-setup";
import { soundtrack } from "./browser/music";
import {
  loadPreferences,
  preferencesToBattleConfig,
  savePreferences,
  type UserPreferences,
} from "./browser/preferences";
import { clearSavedRun, loadSavedRun } from "./browser/storage";
import { UiNavigator } from "./browser/ui-navigation";
import { loadProfile } from "./browser/profile-storage";
import {
  localizeEgg,
  localizeItem,
  localizeWeapon,
} from "./content-i18n";
import {
  EGGS,
  EGG_BY_ID,
  ITEMS,
  ITEM_BY_ID,
  WEAPONS,
  WEAPON_BY_ID,
} from "./core/content";
import {
  ACHIEVEMENTS,
  type PlayerProfile,
  type UnlockReward,
} from "./core/profile";
import { DEFAULT_BATTLE_CONFIG } from "./core/simulation";
import {
  applyDocumentLocale,
  isLocale,
  languageOptions,
  t,
  type Locale,
} from "./i18n";

const app = document.querySelector<HTMLDivElement>("#app");

if (!app) {
  throw new Error("Eggato could not start because #app is missing.");
}

let preferences = loadPreferences();
let profile = loadProfile();
new UiNavigator();
analytics.initialize();
window.addEventListener("pointerdown", unlockSoundtrack, { once: true });
window.addEventListener("keydown", unlockSoundtrack, { once: true });
renderHome();

function renderHome(): void {
  profile = loadProfile();
  const locale = preferences.locale;
  soundtrack.setVolume(preferences.masterVolume);
  soundtrack.setState("menu");
  applyDocumentLocale(locale);
  document.title = `${t(locale, "home.title")}: ${t(locale, "home.subtitle")}`;
  app!.innerHTML = homeMarkup(locale);

  const savedRun = loadSavedRun();
  const continueButton =
    app!.querySelector<HTMLButtonElement>("[data-continue]");
  if (continueButton) continueButton.disabled = savedRun === null;
  populateSettings(preferences);

  app!.querySelector<HTMLSelectElement>("[data-language]")?.addEventListener(
    "change",
    (event) => {
      const nextLocale = (event.target as HTMLSelectElement).value;
      if (!isLocale(nextLocale)) return;
      updateLocale(nextLocale);
      renderHome();
    },
  );

  app!.querySelector<HTMLButtonElement>("[data-start]")?.addEventListener(
    "click",
    () => {
      analytics.track("run_setup_opened");
      app!.dispatchEvent(
        new CustomEvent("egg-arena:start", { bubbles: true }),
      );
      const showRunSetup = (): void => {
        mountRunSetup(app!, {
          locale: preferences.locale,
          profile,
          onComplete: (selection) => {
            clearSavedRun();
            preferences = {
              ...preferences,
              dangerLevel: selection.dangerLevel,
            };
            savePreferences(preferences);
            const config = {
              ...currentBattleConfig(preferences),
              dangerLevel: selection.dangerLevel,
              startingWeaponId: selection.weaponId,
              endless: selection.endless,
            };
            new EggArenaApp(
              app!,
              Date.now(),
              config,
              selection.eggId,
              undefined,
              preferences,
            );
          },
          onCancel: renderHome,
          onLocaleChange: (nextLocale) => {
            updateLocale(nextLocale);
            applyDocumentLocale(nextLocale);
            showRunSetup();
          },
        });
      };
      showRunSetup();
    },
  );

  continueButton?.addEventListener("click", () => {
    const save = loadSavedRun();
    if (!save) return;
    new EggArenaApp(
      app!,
      save.state.seed,
      save.config,
      save.state.eggId,
      save.state,
      preferences,
    );
  });

  const settingsDialog =
    app!.querySelector<HTMLDialogElement>("#settings-dialog");
  app!.querySelector("[data-settings]")?.addEventListener("click", () => {
    settingsDialog?.showModal();
  });
  const progressDialog =
    app!.querySelector<HTMLDialogElement>("#progress-dialog");
  app!.querySelector("[data-progress]")?.addEventListener("click", () => {
    progressDialog?.showModal();
  });
  app!
    .querySelector("[data-close-progress]")
    ?.addEventListener("click", () => progressDialog?.close());
  progressDialog?.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const tab = target.closest<HTMLElement>("[data-progress-tab]");
    if (tab?.dataset.progressTab) {
      const activeTab = tab.dataset.progressTab;
      progressDialog
        .querySelectorAll<HTMLElement>("[data-progress-tab]")
        .forEach((button) => {
          button.setAttribute(
            "aria-selected",
            `${button.dataset.progressTab === activeTab}`,
          );
        });
      progressDialog
        .querySelectorAll<HTMLElement>("[data-progress-panel]")
        .forEach((panel) => {
          panel.hidden = panel.dataset.progressPanel !== activeTab;
        });
    }
    const card = target.closest<HTMLElement>("[data-compendium-detail]");
    if (card) updateCompendiumDetail(progressDialog, card);
  });
  const creditsDialog =
    app!.querySelector<HTMLDialogElement>("#credits-dialog");
  app!.querySelector("[data-credits]")?.addEventListener("click", () => {
    creditsDialog?.showModal();
  });
  app!
    .querySelector("[data-close-credits]")
    ?.addEventListener("click", () => creditsDialog?.close());
  app!
    .querySelector("[data-close-settings]")
    ?.addEventListener("click", () => settingsDialog?.close());

  const settingsForm =
    app!.querySelector<HTMLFormElement>("#settings-form");
  settingsForm?.addEventListener("input", updateSettingsOutputs);
  settingsForm
    ?.querySelector<HTMLSelectElement>('[name="locale"]')
    ?.addEventListener("change", (event) => {
      const nextLocale = (event.target as HTMLSelectElement).value;
      if (!isLocale(nextLocale)) return;

      const draft = readSettingsForm(settingsForm, nextLocale);
      updateLocale(nextLocale);
      renderHome();
      populateSettings(draft);
      app!
        .querySelector<HTMLDialogElement>("#settings-dialog")
        ?.showModal();
    });
  settingsForm?.addEventListener("submit", () => {
    preferences = readSettingsForm(settingsForm, preferences.locale);
    savePreferences(preferences);
    window.setTimeout(renderHome, 0);
  });
}

function unlockSoundtrack(): void {
  soundtrack.setVolume(preferences.masterVolume);
  soundtrack.unlock();
  window.removeEventListener("pointerdown", unlockSoundtrack);
  window.removeEventListener("keydown", unlockSoundtrack);
}

function homeMarkup(locale: Locale): string {
  return `
    <main class="shell" aria-labelledby="game-title">
      <label class="language-picker top-language-picker">
        <span>${t(locale, "language.label")}</span>
        <select data-language aria-label="${t(locale, "language.label")}">
          ${languageOptions(locale)}
        </select>
      </label>
      <section class="hero">
        <p class="eyebrow">${t(locale, "home.eyebrow")}</p>
        <h1 id="game-title">${t(locale, "home.title")}:<br /><span>${t(locale, "home.subtitle")}</span></h1>
        <p class="lede">${t(locale, "home.lede")}</p>
        <div class="menu-actions">
          <button class="primary" type="button" data-start>${t(locale, "home.start")}</button>
          <button class="secondary-button menu-button" type="button" data-continue>${t(locale, "home.continue")}</button>
          <button class="text-button" type="button" data-settings>${t(locale, "home.settings")}</button>
          <button class="text-button" type="button" data-progress>${locale === "zh-CN" ? "成就与解锁" : "Achievements & Unlocks"}</button>
          <button class="text-button" type="button" data-credits>${locale === "zh-CN" ? "制作与版权" : "Credits"}</button>
        </div>
        <p class="hint">${t(locale, "home.hint")}</p>
      </section>
      <section class="preview" aria-label="${t(locale, "home.preview")}">
        <div class="wave-chip">${t(locale, "game.wave", { wave: "01 / 20" })}</div>
        ${spriteMarkup("egg", "fresh-egg", "egg home-hero-egg")}
        ${spriteMarkup("enemy", "mite", "monster monster-a home-enemy-art")}${spriteMarkup("enemy", "scuttler", "monster monster-b home-enemy-art")}
        <div class="pickup pickup-a"></div><div class="pickup pickup-b"></div>
        <div class="floor-line floor-line-a"></div><div class="floor-line floor-line-b"></div>
        <div class="status"><span>${t(locale, "shell.label")}</span><div><i></i></div></div>
      </section>
    </main>
    <dialog id="progress-dialog" class="settings-dialog progress-dialog">
      <div class="progress-panel">
        <div class="settings-heading">
          <p class="eyebrow">${locale === "zh-CN" ? "局外成长" : "META PROGRESSION"}</p>
          <h2>${locale === "zh-CN" ? "成就与解锁" : "Achievements & Unlocks"}</h2>
          <button type="button" data-close-progress aria-label="${locale === "zh-CN" ? "关闭" : "Close"}">×</button>
        </div>
        ${progressMarkup(locale, profile)}
      </div>
    </dialog>
    <dialog id="settings-dialog" class="settings-dialog">
      <form id="settings-form" method="dialog">
        <div class="settings-heading">
          <p class="eyebrow">${t(locale, "settings.eyebrow")}</p>
          <h2>${t(locale, "settings.title")}</h2>
          <button type="button" data-close-settings aria-label="${t(locale, "settings.close")}">×</button>
        </div>
        <section class="settings-group">
          <h3>${locale === "zh-CN" ? "通用" : "General"}</h3>
          <label class="settings-language-row">
            <span>${t(locale, "language.label")}</span>
            <select name="locale">${languageOptions(locale)}</select>
          </label>
        </section>
        <section class="settings-group">
          <h3>${locale === "zh-CN" ? "音频" : "Audio"}</h3>
          <label><span>${t(locale, "settings.volume")} <output data-output="master-volume">70%</output></span><input name="master-volume" type="range" min="0" max="100" step="5" /></label>
        </section>
        <section class="settings-group assist-settings">
          <h3>${locale === "zh-CN" ? "玩法 · 自定义辅助" : "Gameplay · Custom Assist"}</h3>
          <p>${locale === "zh-CN" ? "官方危险等级只在开局时选择。下列倍率属于自定义辅助，不代表危险等级成绩。" : "Official Danger is selected during run setup. These custom multipliers are assists and do not represent a Danger clear."}</p>
          <label><span>${t(locale, "settings.health")} <output data-output="enemy-health">100%</output></span><input name="enemy-health" type="range" min="25" max="200" step="5" /></label>
          <label><span>${t(locale, "settings.speed")} <output data-output="enemy-speed">100%</output></span><input name="enemy-speed" type="range" min="50" max="150" step="5" /></label>
          <label><span>${t(locale, "settings.damage")} <output data-output="enemy-damage">100%</output></span><input name="enemy-damage" type="range" min="25" max="200" step="5" /></label>
        </section>
        <section class="settings-group">
          <h3>${locale === "zh-CN" ? "无障碍" : "Accessibility"}</h3>
          <label class="toggle-row"><span>${t(locale, "settings.shake")}</span><input name="screen-shake" type="checkbox" /></label>
          <label class="toggle-row"><span>${t(locale, "settings.reduced")}</span><input name="reduced-effects" type="checkbox" /></label>
        </section>
        <button class="primary settings-save" type="submit">${t(locale, "settings.save")}</button>
      </form>
    </dialog>
    <dialog id="credits-dialog" class="settings-dialog credits-dialog">
      <section class="credits-panel">
        <div class="settings-heading">
          <p class="eyebrow">EGGATO</p>
          <h2>${locale === "zh-CN" ? "制作与版权" : "Credits"}</h2>
          <button type="button" data-close-credits aria-label="${locale === "zh-CN" ? "关闭" : "Close"}">×</button>
        </div>
        <p>${locale === "zh-CN" ? "一款原创蛋主题桌面竞技场生存游戏。玩法节奏参考同类生存游戏，角色、名称、美术、图标、代码与声音均为 Eggato 原创实现。" : "An original egg-themed desktop arena-survival game. Genre conventions inform the loop; Eggato's characters, names, art, icons, code, and sound are original."}</p>
        <small>© 2026 Eggato · axing22.com</small>
      </section>
    </dialog>`;
}

function updateLocale(locale: Locale): void {
  preferences = { ...preferences, locale };
  savePreferences(preferences);
}

function currentBattleConfig(preference: UserPreferences) {
  const testWaveSeconds = import.meta.env.DEV
    ? Number(new URLSearchParams(window.location.search).get("testWaveSeconds"))
    : Number.NaN;
  const waveSeconds =
    Number.isFinite(testWaveSeconds) && testWaveSeconds > 0
      ? testWaveSeconds
      : DEFAULT_BATTLE_CONFIG.waveSeconds;
  return preferencesToBattleConfig(preference, waveSeconds, profile);
}

function populateSettings(value: UserPreferences): void {
  setInputValue("enemy-health", value.enemyHealthPercent);
  setInputValue("enemy-speed", value.enemySpeedPercent);
  setInputValue("enemy-damage", value.enemyDamagePercent);
  setInputValue("master-volume", value.masterVolume);
  const locale = app?.querySelector<HTMLSelectElement>('[name="locale"]');
  const screenShake = app?.querySelector<HTMLInputElement>('[name="screen-shake"]');
  const reducedEffects = app?.querySelector<HTMLInputElement>('[name="reduced-effects"]');
  if (locale) locale.value = value.locale;
  if (screenShake) screenShake.checked = value.screenShake;
  if (reducedEffects) reducedEffects.checked = value.reducedEffects;
  updateSettingsOutputs();
}

function readSettingsForm(
  form: HTMLFormElement,
  fallbackLocale: Locale,
): UserPreferences {
  const data = new FormData(form);
  const localeValue = data.get("locale");
  return {
    locale: isLocale(localeValue) ? localeValue : fallbackLocale,
    enemyHealthPercent: Number(data.get("enemy-health")),
    enemySpeedPercent: Number(data.get("enemy-speed")),
    enemyDamagePercent: Number(data.get("enemy-damage")),
    dangerLevel: preferences.dangerLevel,
    masterVolume: Number(data.get("master-volume")),
    screenShake: data.get("screen-shake") === "on",
    reducedEffects: data.get("reduced-effects") === "on",
  };
}

function progressMarkup(locale: Locale, value: PlayerProfile): string {
  const completed = new Set(value.completedAchievementIds);
  const unlockedEggs = new Set(value.unlockedEggIds);
  const unlockedWeapons = new Set(value.unlockedWeaponIds);
  const unlockedItems = new Set(value.unlockedItemIds);
  const zh = locale === "zh-CN";
  return `
    <div class="progress-summary">
      <b>${locale === "zh-CN" ? "通关" : "WINS"}<span>${value.totalVictories}</span></b>
      <b>${locale === "zh-CN" ? "最高波次" : "BEST WAVE"}<span>${value.highestWave}</span></b>
      <b>${locale === "zh-CN" ? "角色" : "EGGS"}<span>${value.unlockedEggIds.length}/${EGG_BY_ID.size}</span></b>
      <b>${locale === "zh-CN" ? "危险" : "DANGER"}<span>${value.unlockedDangerLevel}/5</span></b>
    </div>
    <nav class="progress-tabs" role="tablist" aria-label="${zh ? "图鉴分类" : "Compendium categories"}">
      <button type="button" role="tab" aria-selected="true" data-progress-tab="eggs">${zh ? "角色" : "Eggs"} <b>${value.unlockedEggIds.length}/${EGGS.length}</b></button>
      <button type="button" role="tab" aria-selected="false" data-progress-tab="weapons">${zh ? "武器" : "Weapons"} <b>${value.unlockedWeaponIds.length}/${WEAPONS.length}</b></button>
      <button type="button" role="tab" aria-selected="false" data-progress-tab="items">${zh ? "物品" : "Items"} <b>${value.unlockedItemIds.length}/${ITEMS.length}</b></button>
      <button type="button" role="tab" aria-selected="false" data-progress-tab="achievements">${zh ? "成就" : "Achievements"} <b>${completed.size}/${ACHIEVEMENTS.length}</b></button>
    </nav>
    <section class="compendium-panel" data-progress-panel="eggs">
      <div class="compendium-grid egg-compendium-grid">
        ${EGGS.map((egg) => {
          const unlocked = unlockedEggs.has(egg.id);
          const localized = localizeEgg(locale, egg);
          const wins = value.victoriesByEgg[egg.id] ?? 0;
          return catalogCard(
            "egg",
            egg.id,
            localized.name,
            localized.description,
            unlocked,
            unlocked
              ? `${localized.archetype} · ${zh ? `通关 ${wins} 次` : `${wins} wins`}`
              : unlockHint(locale, "eggId", egg.id),
          );
        }).join("")}
      </div>
    </section>
    <section class="compendium-panel" data-progress-panel="weapons" hidden>
      <div class="compendium-grid">
        ${WEAPONS.map((weapon) => {
          const unlocked = unlockedWeapons.has(weapon.id);
          const localized = localizeWeapon(locale, weapon);
          return catalogCard(
            "weapon",
            weapon.id,
            localized.name,
            localized.description,
            unlocked,
            unlocked
              ? `${weapon.family.toUpperCase()} · ${weapon.damage} DMG · ${(weapon.intervalTicks / 60).toFixed(2)}s`
              : unlockHint(locale, "weaponId", weapon.id),
          );
        }).join("")}
      </div>
    </section>
    <section class="compendium-panel" data-progress-panel="items" hidden>
      <div class="compendium-grid">
        ${ITEMS.map((item) => {
          const unlocked = unlockedItems.has(item.id);
          const localized = localizeItem(locale, item);
          return catalogCard(
            "item",
            item.id,
            localized.name,
            localized.description,
            unlocked,
            unlocked
              ? `${zh ? "稀有度" : "RARITY"} T${item.rarity}`
              : unlockHint(locale, "itemId", item.id),
          );
        }).join("")}
      </div>
    </section>
    <section class="achievement-list compendium-panel" data-progress-panel="achievements" hidden>
      ${ACHIEVEMENTS.map((definition) => {
        const done = completed.has(definition.id);
        const title = locale === "zh-CN" ? definition.titleZh : definition.title;
        const description = locale === "zh-CN" ? definition.descriptionZh : definition.description;
        const rewards = definition.reward
          ? rewardNames(locale, definition.reward)
          : [];
        return `
          <article class="achievement-row ${done ? "is-complete" : ""}">
            <i>${done ? "✓" : "◇"}</i>
            <div><b>${title}</b><span>${description}</span>${rewards.length > 0 ? `<em>${locale === "zh-CN" ? "奖励" : "Reward"}：${rewards.join(" · ")}</em>` : ""}</div>
          </article>`;
      }).join("")}
    </section>
    <aside class="compendium-detail" data-compendium-detail-panel>
      <p>${zh ? "图鉴详情" : "COMPENDIUM DETAIL"}</p>
      <h3>${zh ? "选择一个条目" : "Select an entry"}</h3>
      <span>${zh ? "查看状态、属性和解锁条件。" : "Inspect its status, traits, and unlock condition."}</span>
      <em></em>
    </aside>`;
}

function catalogCard(
  kind: "egg" | "weapon" | "item",
  id: string,
  title: string,
  body: string,
  unlocked: boolean,
  status: string,
): string {
  return `
    <button class="compendium-card ${unlocked ? "is-unlocked" : "is-locked"}" type="button" data-compendium-detail>
      ${spriteMarkup(kind, id, "compendium-art")}
      <b class="compendium-card-title">${title}</b>
      <span class="compendium-card-body">${body}</span>
      <em class="compendium-card-status">${unlocked ? "✓ " : "◇ "}${status}</em>
    </button>`;
}

function unlockHint(
  locale: Locale,
  rewardKey: "eggId" | "weaponId" | "itemId",
  id: string,
): string {
  const achievement = ACHIEVEMENTS.find(
    (entry) => entry.reward?.[rewardKey] === id,
  );
  if (!achievement) return locale === "zh-CN" ? "默认内容" : "Default content";
  return locale === "zh-CN"
    ? `解锁：${achievement.descriptionZh}`
    : `Unlock: ${achievement.description}`;
}

function updateCompendiumDetail(
  dialog: HTMLDialogElement,
  card: HTMLElement,
): void {
  const panel = dialog.querySelector<HTMLElement>(
    "[data-compendium-detail-panel]",
  );
  if (!panel) return;
  const title = card.querySelector(".compendium-card-title")?.textContent ?? "";
  const body = card.querySelector(".compendium-card-body")?.textContent ?? "";
  const status = card.querySelector(".compendium-card-status")?.textContent ?? "";
  const heading = panel.querySelector("h3");
  const description = panel.querySelector("span");
  const state = panel.querySelector("em");
  if (heading) heading.textContent = title;
  if (description) description.textContent = body;
  if (state) state.textContent = status;
  dialog
    .querySelectorAll(".compendium-card.is-selected")
    .forEach((entry) => entry.classList.remove("is-selected"));
  card.classList.add("is-selected");
}

function rewardNames(locale: Locale, reward: UnlockReward): string[] {
  const result: string[] = [];
  const egg = reward.eggId ? EGG_BY_ID.get(reward.eggId) : undefined;
  const weapon = reward.weaponId ? WEAPON_BY_ID.get(reward.weaponId) : undefined;
  const item = reward.itemId ? ITEM_BY_ID.get(reward.itemId) : undefined;
  if (egg) result.push(localizeEgg(locale, egg).name);
  if (weapon) result.push(localizeWeapon(locale, weapon).name);
  if (item) result.push(localizeItem(locale, item).name);
  return result;
}

function setInputValue(name: string, value: number): void {
  const input = app?.querySelector<HTMLInputElement>(`[name="${name}"]`);
  if (input) input.value = `${value}`;
}

function updateSettingsOutputs(): void {
  const mappings = [
    ["enemy-health", "enemy-health"],
    ["enemy-speed", "enemy-speed"],
    ["enemy-damage", "enemy-damage"],
    ["master-volume", "master-volume"],
  ] as const;
  for (const [inputName, outputName] of mappings) {
    const input = app?.querySelector<HTMLInputElement>(`[name="${inputName}"]`);
    const output = app?.querySelector<HTMLOutputElement>(`[data-output="${outputName}"]`);
    if (input && output) output.value = `${input.value}%`;
  }
}
